import React, { useCallback, useEffect, useState } from 'react';
import CodeBlockWithCopy from './CodeBlockWithCopy';
import { parseContentWithCode } from '../../utils/parseContentWithCode';

export default function MessageBubble({ message, isUser, onSuggestionClick, onFeedback, uiVariant = 'dark' }) {
  const { content, links = [], snippets = [], streaming, suggestions = [], attachments = [], sources = [], confidenceLow = false } = message;
  const isLight = uiVariant === 'light';
  const hasInlineCode = content && /```[\s\S]*?```/.test(content);
  const parts = hasInlineCode ? parseContentWithCode(content) : null;
  const feedback = message?.feedback || null;
  const speakerLabel = isUser ? 'Utilisateur' : 'Assistant';
  const [previewIndex, setPreviewIndex] = useState(-1);
  const hasAttachments = Array.isArray(attachments) && attachments.length > 0;
  const previewImage = hasAttachments && previewIndex >= 0 ? attachments[previewIndex] : null;
  const hasMaskedLinks = !isUser && typeof content === 'string' && content.includes('[URL non vérifiée');

  /** Pills sources : palette ambre centrée + nuance par score (évite arc-en-ciel vert/orange/rouge). */
  function sourceClassFromScore(score) {
    if (typeof score !== 'number') {
      return isLight
        ? 'border-slate-200 bg-white text-slate-600'
        : 'border-slate-600/80 bg-slate-800/60 text-slate-300';
    }
    if (score >= 0.25) {
      return isLight
        ? 'border-amber-400/55 bg-amber-50 text-amber-900'
        : 'border-amber-400/45 bg-amber-500/15 text-amber-100';
    }
    if (score >= 0.12) {
      return isLight
        ? 'border-amber-300/70 bg-amber-50/90 text-amber-800'
        : 'border-amber-500/30 bg-amber-500/5 text-amber-200';
    }
    return isLight
      ? 'border-amber-200 bg-amber-50/80 text-amber-800/90'
      : 'border-amber-500/20 bg-amber-950/40 text-amber-200/90';
  }

  const goPrevPreview = useCallback(() => {
    if (!hasAttachments) return;
    setPreviewIndex((idx) => {
      var next = idx - 1;
      if (next < 0) next = attachments.length - 1;
      return next;
    });
  }, [hasAttachments, attachments]);

  const goNextPreview = useCallback(() => {
    if (!hasAttachments) return;
    setPreviewIndex((idx) => {
      var next = idx + 1;
      if (next >= attachments.length) next = 0;
      return next;
    });
  }, [hasAttachments, attachments]);

  useEffect(() => {
    if (previewIndex < 0) return;
    const onEsc = function (e) {
      if (e.key === 'Escape') setPreviewIndex(-1);
      if (e.key === 'ArrowLeft') goPrevPreview();
      if (e.key === 'ArrowRight') goNextPreview();
    };
    document.addEventListener('keydown', onEsc);
    return function () {
      document.removeEventListener('keydown', onEsc);
    };
  }, [previewIndex, goPrevPreview, goNextPreview]);

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
      role="article"
      aria-label={`Message ${speakerLabel}`}
    >
      <div
        className={`max-w-[86%] rounded-2xl px-5 py-3.5 transition-all duration-200 ${
          isUser
            ? isLight
              ? 'bg-amber-50 border border-amber-200/90 text-slate-900 rounded-br-md shadow-[0_4px_20px_-12px_rgba(15,23,42,0.08)]'
              : 'bg-amber-500/22 border border-amber-400/35 text-amber-50 rounded-br-md shadow-[0_4px_20px_-12px_rgba(0,0,0,0.35)]'
            : isLight
              ? 'bg-slate-50/95 text-slate-900 border border-slate-200 rounded-bl-md shadow-[0_4px_24px_-12px_rgba(15,23,42,0.08)]'
              : 'bg-slate-800/55 text-slate-100 border border-slate-600/70 rounded-bl-md shadow-[0_4px_24px_-14px_rgba(0,0,0,0.45)]'
        }`}
      >
        {parts ? (
          <div className="text-[15px] leading-[1.6] space-y-2">
            {parts.map((p, i) =>
              p.type === 'text' ? (
                <p
                  key={i}
                  className={`whitespace-pre-wrap break-words leading-relaxed ${
                    isUser ? (isLight ? 'text-slate-900/95' : 'text-white/95') : isLight ? 'text-slate-800/95' : 'text-slate-200'
                  }`}
                >
                  {p.value}
                </p>
              ) : (
                <CodeBlockWithCopy key={i} code={p.value} language={p.language} uiVariant={uiVariant} />
              )
            )}
          </div>
        ) : (
          <p
            className={`text-[15px] leading-[1.6] whitespace-pre-wrap break-words ${
              isUser
                ? isLight
                  ? 'text-slate-900/95'
                  : 'text-white/95'
                : isLight
                  ? 'text-slate-800'
                  : 'text-slate-200'
            }`}
          >
            {content}
            {streaming && (
              <span
                className="inline-block w-2 h-4 ml-0.5 bg-amber-400 animate-pulse rounded"
                aria-hidden="true"
              />
            )}
          </p>
        )}
        {links.length > 0 && (
          <ul className="mt-2 space-y-1">
            {links.map((href, i) => (
              <li key={i}>
                <a
                  href={href}
                  className={`text-sm underline decoration-amber-400/55 hover:decoration-amber-300 ${
                    isUser
                      ? isLight
                        ? 'text-amber-900 hover:text-slate-900'
                        : 'text-amber-100 hover:text-white'
                      : isLight
                        ? 'text-amber-700 hover:text-amber-600'
                        : 'text-amber-400 hover:text-amber-300'
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Lien utile ${i + 1} : ${href}`}
                >
                  {href}
                </a>
              </li>
            ))}
          </ul>
        )}
        {snippets.length > 0 && (
          <div className="mt-2 space-y-2">
            {snippets.map((s, i) => (
              <CodeBlockWithCopy
                key={i}
                code={s.code || ''}
                language={s.language || 'text'}
                uiVariant={uiVariant}
              />
            ))}
          </div>
        )}
        {hasMaskedLinks && !streaming && (
          <div
            className={`mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold ${
              isLight
                ? 'border-amber-300/60 bg-amber-50 text-amber-900'
                : 'border-amber-400/35 bg-amber-500/10 text-amber-200'
            }`}
            role="status"
            aria-label="Certains liens ont été masqués"
            title="Certains liens ont été masqués car non vérifiables automatiquement"
          >
            <span aria-hidden="true">Liens masqués</span>
            <span className={isLight ? 'text-amber-800/70 font-medium' : 'text-amber-200/70 font-medium'}>
              vérifier la doc
            </span>
          </div>
        )}
        {!isUser && (confidenceLow || (Array.isArray(sources) && sources.length > 0)) && (
          <div
            className={`mt-3 rounded-xl border p-2.5 ${
              isLight ? 'border-slate-200/90 bg-white/90' : 'border-slate-600/60 bg-slate-900/35'
            }`}
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div
                className={`text-[11px] uppercase tracking-[0.14em] font-medium ${
                  isLight ? 'text-slate-500' : 'text-slate-400'
                }`}
              >
                Sources consultées
              </div>
              {confidenceLow && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${
                    isLight ? 'border-amber-400/40 bg-amber-500/10 text-amber-700' : 'border-amber-400/40 bg-amber-500/10 text-amber-300'
                  }`}
                >
                  Confiance faible
                </span>
              )}
            </div>
            {Array.isArray(sources) && sources.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {sources.map((src, i) => {
                  var name = typeof src === 'string' ? src : src?.name || 'source';
                  var score = typeof src === 'object' && typeof src?.score === 'number' ? src.score : null;
                  var url = typeof src === 'object' ? src?.url : null;
                  var label = score != null ? `${name} (${score.toFixed(2)})` : name;
                  var scoreClass = sourceClassFromScore(score);
                  return url ? (
                    <a
                      key={`${name}-${i}`}
                      href={url}
                      className={`text-[11px] px-2.5 py-1 rounded-full border font-medium hover:opacity-90 transition-opacity ${scoreClass}`}
                      aria-label={`Source ${i + 1}: ${name}`}
                    >
                      {label}
                    </a>
                  ) : (
                    <span key={`${name}-${i}`} className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${scoreClass}`}>
                      {label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {Array.isArray(attachments) && attachments.length > 0 && (
          <div className="mt-3">
            <div
              className={`text-[11px] uppercase tracking-[0.12em] mb-2 ${
                isUser ? 'text-white/80' : isLight ? 'text-slate-500' : 'text-slate-400'
              }`}
            >
              Pièces jointes
            </div>
            <div className="grid grid-cols-2 gap-2">
              {attachments.map((att, i) => (
                <div
                  key={`${att.name || 'img'}-${i}`}
                  className={`rounded-xl border p-1.5 ${
                    isUser
                      ? 'border-white/20 bg-white/10'
                      : isLight
                        ? 'border-slate-200 bg-slate-50'
                        : 'border-slate-700/60 bg-slate-900/25'
                  }`}
                >
                  <img
                    src={att.dataUrl}
                    alt={att.name ? `Pièce jointe ${att.name}` : 'Pièce jointe image'}
                    className={`w-full h-24 object-cover rounded-lg cursor-zoom-in ${
                      isUser ? 'border border-white/10' : isLight ? 'border border-slate-200' : 'border border-white/10'
                    }`}
                    onClick={() => setPreviewIndex(i)}
                  />
                  <div className="mt-1 px-0.5">
                    <p className={`text-[11px] truncate ${isUser ? 'text-white/90' : isLight ? 'text-slate-700' : 'text-slate-300'}`}>
                      {att.name || 'image'}
                    </p>
                    {att.sensitiveWarning && (
                      <p className={`text-[10px] ${isLight ? 'text-amber-700' : 'text-amber-300'}`}>Potentiellement sensible</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {previewImage && (
          <div
            className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Aperçu image"
            onClick={() => setPreviewIndex(-1)}
          >
            <div
              className="max-w-5xl max-h-[90vh] w-full flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {attachments.length > 1 && (
                <div className="w-full flex items-center justify-between mb-3">
                  <button
                    type="button"
                    onClick={goPrevPreview}
                    className="h-9 px-3 rounded-lg border border-white/20 bg-black/35 text-white hover:bg-black/55 transition-colors"
                    aria-label="Image précédente"
                  >
                    ← Précédente
                  </button>
                  <div className="text-xs text-slate-200 bg-black/40 rounded-lg px-3 py-1.5">
                    {previewIndex + 1} / {attachments.length}
                  </div>
                  <button
                    type="button"
                    onClick={goNextPreview}
                    className="h-9 px-3 rounded-lg border border-white/20 bg-black/35 text-white hover:bg-black/55 transition-colors"
                    aria-label="Image suivante"
                  >
                    Suivante →
                  </button>
                </div>
              )}
              <img
                src={previewImage.dataUrl}
                alt={`Aperçu plein écran ${previewImage.name}`}
                className="max-h-[80vh] w-auto max-w-full rounded-xl border border-white/20 shadow-2xl"
              />
              <div className="mt-3 text-xs text-slate-200 bg-black/40 rounded-lg px-3 py-1.5">
                {previewImage.name} - Echap pour fermer{attachments.length > 1 ? ' - ← / → pour naviguer' : ''}
              </div>
            </div>
          </div>
        )}
        {!isUser && suggestions.length > 0 && onSuggestionClick && (
          <div className="mt-3 flex flex-wrap gap-2">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onSuggestionClick(s)}
                className={`text-xs px-3 py-2 rounded-xl border transition-all duration-200 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 ${
                  isLight
                    ? 'bg-white/70 text-slate-700 hover:bg-amber-400/10 border-slate-200 hover:border-amber-400/35'
                    : 'bg-slate-700/40 text-amber-300 hover:bg-amber-400/15 border-slate-600/60 hover:border-amber-400/35'
                }`}
                aria-label={`Suggestion ${i + 1}: ${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {!isUser && !streaming && onFeedback && (
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => onFeedback('up')}
              className={`h-8 px-2.5 rounded-lg text-xs border transition-colors ${
                feedback === 'up'
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40'
                  : isLight
                    ? 'bg-white/70 text-slate-600 border-slate-200 hover:bg-white/95'
                    : 'bg-slate-800/40 text-slate-300 border-slate-600/70 hover:bg-slate-700/50'
              }`}
              aria-label="Réponse utile"
              title="Réponse utile"
              aria-pressed={feedback === 'up'}
            >
              👍
            </button>
            <button
              type="button"
              onClick={() => onFeedback('down')}
              className={`h-8 px-2.5 rounded-lg text-xs border transition-colors ${
                feedback === 'down'
                  ? 'bg-rose-500/20 text-rose-300 border-rose-400/40'
                  : isLight
                    ? 'bg-white/70 text-slate-600 border-slate-200 hover:bg-white/95'
                    : 'bg-slate-800/40 text-slate-300 border-slate-600/70 hover:bg-slate-700/50'
              }`}
              aria-label="Réponse à améliorer"
              title="Réponse à améliorer"
              aria-pressed={feedback === 'down'}
            >
              👎
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
